import { FC, useMemo, useState } from 'react';

interface Model {
  modelName: string;
  context: string;
  provider: string;
  inputTokenCostPerThousand: number;
  outputTokenCostPerThousand: number;
}

interface Category {
  [key: string]: Model[];
}

interface Option {
  name: string;
  multiplier: number;
}

interface PricingCalculatorProps {
  t: any;
  selectedOption: Option;
  inputTokens: number;
  outputTokens: number;
  apiCalls: number;
}

const PricingCalculator: FC<PricingCalculatorProps> = ({
  t,
  selectedOption,
  inputTokens,
  outputTokens,
  apiCalls,
}) => {
  const [activeCategory, setActiveCategory] = useState<string>(
    'Chat/Completion Models',
  );
  const [sortField, setSortField] = useState<string>('provider');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const calculateCost = (model: Model): number => {
    return (
      (model.inputTokenCostPerThousand || 0) *
        ((inputTokens * selectedOption.multiplier) / 1000) +
      (model.outputTokenCostPerThousand || 0) *
        ((outputTokens * selectedOption.multiplier) / 1000)
    );
  };

  const sortModels = (models: Category[number]): Model[] => {
    return models.sort((a, b) => {
      if (sortField === 'provider') {
        return sortDirection === 'asc'
          ? a.provider.localeCompare(b.provider)
          : b.provider.localeCompare(a.provider);
      }
      if (sortField === 'modelName') {
        return sortDirection === 'asc'
          ? a.modelName.localeCompare(b.modelName)
          : b.modelName.localeCompare(a.modelName);
      }
      if (sortField === 'input') {
        return sortDirection === 'asc'
          ? a.inputTokenCostPerThousand - b.inputTokenCostPerThousand
          : b.inputTokenCostPerThousand - a.inputTokenCostPerThousand;
      }
      if (sortField === 'output') {
        return sortDirection === 'asc'
          ? a.outputTokenCostPerThousand - b.outputTokenCostPerThousand
          : b.outputTokenCostPerThousand - a.outputTokenCostPerThousand;
      }
      if (sortField === 'perCall') {
        return sortDirection === 'asc'
          ? calculateCost(a) - calculateCost(b)
          : calculateCost(b) - calculateCost(a);
      }
      // Default to total
      return sortDirection === 'asc'
        ? calculateCost(a) * apiCalls - calculateCost(b) * apiCalls
        : calculateCost(b) * apiCalls - calculateCost(a) * apiCalls;
    });
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filterModels = (models: Model[]): Model[] => {
    if (!searchTerm) return models;

    return models.filter(
      (model) =>
        model.modelName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        model.provider.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  /*  
Gpt-4.1
GPT-4.1 mini
GPT-4.1 nano
Grok 2
*/
  const models: Category = useMemo(
    () => ({
      'Chat/Completion Models': sortModels([
        {
          modelName: 'Grok 2',
          context: '131K',
          provider: 'xAI',
          inputTokenCostPerThousand: 2.0 / 1000,
          outputTokenCostPerThousand: 10.0 / 1000,
        },
        {
          modelName: 'Gpt-4.1 nano',
          context: '1014K/32.7K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 0.1 / 1000,
          outputTokenCostPerThousand: 0.4 / 1000,
        },
        {
          modelName: 'Gpt-4.1 mini',
          context: '1014K/32.7K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 0.4 / 1000,
          outputTokenCostPerThousand: 1.6 / 1000,
        },
        {
          modelName: 'Gpt-4.1',
          context: '1014K/32.7K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 2 / 1000,
          outputTokenCostPerThousand: 8 / 1000,
        },
        {
          modelName: 'Claude 3 Opus',
          context: '200K',
          provider: 'Anthropic',
          inputTokenCostPerThousand: 15 / 1000,
          outputTokenCostPerThousand: 75 / 1000,
        },
        {
          modelName: 'Grok 3',
          context: '131K',
          provider: 'xAI',
          inputTokenCostPerThousand: 3 / 1000,
          outputTokenCostPerThousand: 15 / 1000,
        },
        {
          modelName: 'Grok 3 Mini',
          context: '131K',
          provider: 'xAI',
          inputTokenCostPerThousand: 0.3 / 1000,
          outputTokenCostPerThousand: 0.5 / 1000,
        },
        {
          modelName: 'GPT-4.5',
          context: '128K/16.3K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 75 / 1000,
          outputTokenCostPerThousand: 150 / 1000,
        },
        {
          modelName: 'Anthropic Claude 3.7 Sonnet',
          context: '200K',
          provider: 'Anthropic',
          inputTokenCostPerThousand: 3 / 1000,
          outputTokenCostPerThousand: 15 / 1000,
        },
        {
          modelName: 'Gemini 2.5 Pro Preview',
          context: '1000K/64K',
          provider: 'Google',
          inputTokenCostPerThousand: 2.5 / 1000,
          outputTokenCostPerThousand: 15 / 1000,
        },
        {
          modelName: 'Gemini 2.0 Flash',
          context: '1000K/8K',
          provider: 'Google',
          inputTokenCostPerThousand: 0.1 / 1000,
          outputTokenCostPerThousand: 0.4 / 1000,
        },
        {
          modelName: 'Gemini 2.0 Flash-Lite',
          context: '1000K/8K',
          provider: 'Google',
          inputTokenCostPerThousand: 0.075 / 1000,
          outputTokenCostPerThousand: 0.3 / 1000,
        },
        {
          modelName: 'Command A',
          context: '256K/8K',
          provider: 'Cohere',
          inputTokenCostPerThousand: 2.5 / 1000,
          outputTokenCostPerThousand: 10 / 1000,
        },
        {
          modelName: 'Command R7B',
          context: '128K/4K',
          provider: 'Cohere',
          inputTokenCostPerThousand: 0.0375 / 1000,
          outputTokenCostPerThousand: 0.15 / 1000,
        },
        {
          modelName: 'Llama 4 Scout',
          context: '10000K',
          provider: 'Meta via Deepinfra',
          inputTokenCostPerThousand: 0.08 / 1000,
          outputTokenCostPerThousand: 0.3 / 1000,
        },
        {
          modelName: 'Llama 4 Maverick',
          context: '1000K',
          provider: 'Meta via Deepinfra',
          inputTokenCostPerThousand: 0.18 / 1000,
          outputTokenCostPerThousand: 0.6 / 1000,
        },
        {
          modelName: 'DeepSeek-V3',
          context: '128K/8K',
          provider: 'DeepSeek',
          inputTokenCostPerThousand: 0.14 / 1000,
          outputTokenCostPerThousand: 0.28 / 1000,
        },
        {
          modelName: 'DeepSeek-R1',
          context: '128K/8K',
          provider: 'DeepSeek',
          inputTokenCostPerThousand: 0.55 / 1000,
          outputTokenCostPerThousand: 2.19 / 1000,
        },
        {
          modelName: 'GPT-4o',
          context: '128K/16K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 2.5 / 1000,
          outputTokenCostPerThousand: 10 / 1000,
        },
        {
          modelName: 'GPT-4o mini',
          context: '128K/16K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 0.15 / 1000,
          outputTokenCostPerThousand: 0.6 / 1000,
        },
        {
          modelName: 'o3-mini',
          context: '200K/100K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 1.1 / 1000,
          outputTokenCostPerThousand: 4.4 / 1000,
        },
        {
          modelName: 'o1',
          context: '200K/100K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 15 / 1000,
          outputTokenCostPerThousand: 60 / 1000,
        },
        {
          modelName: 'Claude 3.5 Sonnet',
          context: '200K/8K',
          provider: 'Anthropic',
          inputTokenCostPerThousand: 3 / 1000,
          outputTokenCostPerThousand: 15 / 1000,
        },
        {
          modelName: 'Claude 3.5 Haiku',
          context: '200K/8K',
          provider: 'Anthropic',
          inputTokenCostPerThousand: 0.8 / 1000,
          outputTokenCostPerThousand: 4 / 1000,
        },
        {
          modelName: 'Gemini 1.5 Pro',
          context: '128K',
          provider: 'Google',
          inputTokenCostPerThousand: 1.25 / 1000,
          outputTokenCostPerThousand: 5 / 1000,
        },
        {
          modelName: 'Gemini 1.5 Flash',
          context: '128K',
          provider: 'Google',
          inputTokenCostPerThousand: 0.075 / 1000,
          outputTokenCostPerThousand: 0.3 / 1000,
        },
        {
          modelName: 'Gemini 1.5 Flash-8B',
          context: '128K',
          provider: 'Google',
          inputTokenCostPerThousand: 0.0375 / 1000,
          outputTokenCostPerThousand: 0.15 / 1000,
        },
        {
          modelName: 'Amazon Nova Micro',
          context: '128K',
          provider: 'Amazon',
          inputTokenCostPerThousand: 0.035 / 1000,
          outputTokenCostPerThousand: 0.14 / 1000,
        },
        {
          modelName: 'Amazon Nova Lite',
          context: '300K',
          provider: 'Amazon',
          inputTokenCostPerThousand: 0.06 / 1000,
          outputTokenCostPerThousand: 0.24 / 1000,
        },
        {
          modelName: 'Amazon Nova Pro',
          context: '300K',
          provider: 'Amazon',
          inputTokenCostPerThousand: 0.8 / 1000,
          outputTokenCostPerThousand: 3.2 / 1000,
        },
        {
          modelName: 'Command R',
          context: '128K/4K',
          provider: 'Cohere',
          inputTokenCostPerThousand: 0.5 / 1000,
          outputTokenCostPerThousand: 1.5 / 1000,
        },
        {
          modelName: 'Command R+',
          context: '128K',
          provider: 'Cohere',
          inputTokenCostPerThousand: 3 / 1000,
          outputTokenCostPerThousand: 15 / 1000,
        },
        {
          modelName: 'Llama 3.3 70b',
          context: '128K/2K',
          provider: 'Meta via Deepinfra',
          inputTokenCostPerThousand: 0.23 / 1000,
          outputTokenCostPerThousand: 0.4 / 1000,
        },
        {
          modelName: 'Llama 3.1 405b',
          context: '128K/2K',
          provider: 'Meta via Deepinfra',
          inputTokenCostPerThousand: 1.79 / 1000,
          outputTokenCostPerThousand: 1.79 / 1000,
        },
        {
          modelName: 'Llama 3.2 90b',
          context: '128K/2K',
          provider: 'Meta via Deepinfra',
          inputTokenCostPerThousand: 0.35 / 1000,
          outputTokenCostPerThousand: 0.4 / 1000,
        },
        {
          modelName: 'Llama 3.1 70b',
          context: '128K/2K',
          provider: 'Meta via Deepinfra',
          inputTokenCostPerThousand: 0.23 / 1000,
          outputTokenCostPerThousand: 0.4 / 1000,
        },
        {
          modelName: 'Llama 3.2 11b',
          context: '128K/2K',
          provider: 'Meta via Deepinfra',
          inputTokenCostPerThousand: 0.055 / 1000,
          outputTokenCostPerThousand: 0.055 / 1000,
        },
        {
          modelName: 'Llama 3.3 70b',
          context: '8K/2K',
          provider: 'Meta via Deepinfra/Groq',
          inputTokenCostPerThousand: 0.59 / 1000,
          outputTokenCostPerThousand: 0.79 / 1000,
        },
        {
          modelName: 'Command',
          context: '4K',
          provider: 'Cohere',
          inputTokenCostPerThousand: 10 / 1000,
          outputTokenCostPerThousand: 20 / 1000,
        },
      ]),
      'Audio Models': sortModels([
        {
          modelName: 'GPT-4o Realtime (Audio)',
          context: '128K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 40 / 1000,
          outputTokenCostPerThousand: 80 / 1000,
        },
        {
          modelName: 'GPT-4o Audio',
          context: '128K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 40 / 1000,
          outputTokenCostPerThousand: 80 / 1000,
        },
        {
          modelName: 'GPT-4o Mini Audio',
          context: '128K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 10 / 1000,
          outputTokenCostPerThousand: 20 / 1000,
        },
      ]),
      'Fine-tuning models': sortModels([
        {
          modelName: 'GPT-4o',
          context: '128K/16K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 3.75 / 1000,
          outputTokenCostPerThousand: 15 / 1000,
        },
        {
          modelName: 'GPT-4o Mini',
          context: '128K/16K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 0.3 / 1000,
          outputTokenCostPerThousand: 1.2 / 1000,
        },
        {
          modelName: 'GPT-3.5 Turbo',
          context: '4K',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 3 / 1000,
          outputTokenCostPerThousand: 6 / 1000,
        },
      ]),
      'Embedding models': sortModels([
        {
          modelName: '3 Small',
          context: '',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 0.02 / 1000,
          outputTokenCostPerThousand: 0,
        },
        {
          modelName: 'Ada v2',
          context: '',
          provider: 'OpenAI',
          inputTokenCostPerThousand: 0.1 / 1000,
          outputTokenCostPerThousand: 0,
        },
        {
          modelName: 'Gemini Text Embedding',
          context: '2K',
          provider: 'Google',
          inputTokenCostPerThousand: 0.1 / 1000,
          outputTokenCostPerThousand: 0,
        },
        {
          modelName: 'Embed v3.0',
          context: '',
          provider: 'Cohere',
          inputTokenCostPerThousand: 0.1 / 1000,
          outputTokenCostPerThousand: 0,
        },
        {
          modelName: 'Mistral Embed',
          context: '',
          provider: 'Mistral AI',
          inputTokenCostPerThousand: 0.1 / 1000,
          outputTokenCostPerThousand: 0,
        },
      ]),
    }),
    [sortField, sortDirection],
  );

  // Get current filtered models
  const filteredModels = filterModels(models[activeCategory]);

  // Calculate total costs for all filtered models
  const totalCosts = filteredModels.reduce((sum, model) => {
    return sum + calculateCost(model) * apiCalls;
  }, 0);

  // Calculate average cost
  const averageCost =
    filteredModels.length > 0 ? totalCosts / filteredModels.length : 0;

  return (
    <div className="overflow-x-auto p-2 md:overflow-x-hidden md:p-4">
      {/* Category Navigation & Search */}
      <div className="mb-4">
        <div className="mb-3 flex flex-col items-center justify-between md:flex-row">
          {/* Category Tabs */}
          <div className="mb-3 flex w-full space-x-1.5 overflow-x-auto pb-1 md:mb-0 md:w-auto md:pb-0">
            {Object.keys(models).map((category) => (
              <button
                key={category}
                className={`whitespace-nowrap rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeCategory === category
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="w-full md:w-auto">
            <div className="relative">
              <input
                type="text"
                className="w-full rounded border border-gray-300 py-1.5 pl-8 pr-4 text-sm focus:outline-none focus:ring-1 focus:ring-black md:w-64"
                placeholder={
                  t.searchPlaceholder || 'Search models or providers...'
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th
                scope="col"
                className="cursor-pointer px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors duration-150 hover:bg-gray-200"
                onClick={() => handleSort('provider')}
              >
                <div className="flex items-center">
                  {t.providerLabel}
                  {sortField === 'provider' && (
                    <span className="ml-1 text-blue-600">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th
                scope="col"
                className="cursor-pointer px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors duration-150 hover:bg-gray-200"
                onClick={() => handleSort('modelName')}
              >
                <div className="flex items-center">
                  {t.modelLabel}
                  {sortField === 'modelName' && (
                    <span className="ml-1 text-blue-600">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
              >
                {t.contextLabel}
              </th>
              <th
                scope="col"
                className="hidden cursor-pointer px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors duration-150 hover:bg-gray-200 md:table-cell"
                onClick={() => handleSort('input')}
              >
                <div className="flex items-center">
                  {t.inputCostLabel}
                  {sortField === 'input' && (
                    <span className="ml-1 text-blue-600">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th
                scope="col"
                className="hidden cursor-pointer px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors duration-150 hover:bg-gray-200 md:table-cell"
                onClick={() => handleSort('output')}
              >
                <div className="flex items-center">
                  {t.outputCostLabel}
                  {sortField === 'output' && (
                    <span className="ml-1 text-blue-600">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th
                scope="col"
                className="cursor-pointer px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors duration-150 hover:bg-gray-200"
                onClick={() => handleSort('perCall')}
              >
                <div className="flex items-center justify-end">
                  {t.perCallLabel}
                  {sortField === 'perCall' && (
                    <span className="ml-1 text-blue-600">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th
                scope="col"
                className="cursor-pointer px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 transition-colors duration-150 hover:bg-gray-200"
                onClick={() => handleSort('total')}
              >
                <div className="flex items-center justify-end">
                  {t.totalLabel}
                  {sortField === 'total' && (
                    <span className="ml-1 text-blue-600">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredModels.map((model, modelIdx) => {
              const perCallCost = calculateCost(model);
              const totalCost = perCallCost * apiCalls;
              const modelKey = `${model.provider}-${model.modelName}`;

              return (
                <tr
                  key={modelKey}
                  className={`transition-colors duration-150 ${
                    hoveredRow === modelKey
                      ? 'bg-blue-50'
                      : modelIdx % 2 === 0
                        ? 'bg-white'
                        : 'bg-gray-50'
                  }`}
                  onMouseEnter={() => setHoveredRow(modelKey)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td className="border-r border-gray-100 px-3 py-2 text-sm font-medium text-gray-900">
                    {model.provider}
                  </td>
                  <td className="border-r border-gray-100 px-3 py-2 text-sm text-gray-700">
                    {model.modelName}
                  </td>
                  <td className="border-r border-gray-100 px-3 py-2 text-sm text-gray-500">
                    {model.context}
                  </td>
                  <td className="hidden border-r border-gray-100 px-3 py-2 font-mono text-sm text-gray-500 md:table-cell">
                    $
                    {isNaN(model.inputTokenCostPerThousand)
                      ? '0.00'
                      : model.inputTokenCostPerThousand.toFixed(6)}
                  </td>
                  <td className="hidden border-r border-gray-100 px-3 py-2 font-mono text-sm text-gray-500 md:table-cell">
                    {model.outputTokenCostPerThousand > 0 &&
                      `$${
                        isNaN(model.outputTokenCostPerThousand)
                          ? '0.00'
                          : model.outputTokenCostPerThousand.toFixed(6)
                      }`}
                  </td>
                  <td className="border-r border-gray-100 px-3 py-2 text-right font-mono text-sm text-gray-700">
                    ${isNaN(perCallCost) ? '0.00' : perCallCost.toFixed(4)}
                  </td>
                  <td className="px-3 py-2 text-right text-sm font-medium">
                    <span
                      className={`font-mono font-semibold ${
                        totalCost > 10
                          ? 'text-red-600'
                          : totalCost > 1
                            ? 'text-amber-600'
                            : 'text-green-600'
                      }`}
                    >
                      ${isNaN(totalCost) ? '0.00' : totalCost.toFixed(2)}
                    </span>
                  </td>
                </tr>
              );
            })}
            {filteredModels.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-4 text-center text-sm text-gray-500"
                >
                  {t.noResultsLabel || 'No models found matching your search'}
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="border-t border-gray-200 bg-gray-50">
            <tr>
              <td
                colSpan={6}
                className="px-3 py-2 text-right text-sm font-medium text-gray-700"
              >
                {t.averageLabel || 'Average Cost:'}
              </td>
              <td className="px-3 py-2 text-right font-mono text-sm font-medium text-gray-700">
                ${isNaN(averageCost) ? '0.00' : averageCost.toFixed(2)}
              </td>
            </tr>
            <tr>
              <td
                colSpan={6}
                className="px-3 py-2 text-right text-sm font-medium text-gray-700"
              >
                {t.totalModelsLabel || 'Total Models:'}
              </td>
              <td className="px-3 py-2 text-right text-sm font-medium text-gray-700">
                {filteredModels.length}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default PricingCalculator;
